// ============================================================================
// Componente: FinanceiroRelatorioFechamento.js
// Objetivo: Relatório pronto do Fechamento Mensal — VISÃO FINANCEIRA.
//           Filtros mês + empresa, tabela na tela com paginação e exportação
//           Excel. Espelha o relatório do módulo Contábil, mas é MENSAL
//           (reusa sp_gerar_relatorios_fechamento → results[1], agregado por
//           GRUPO_LOJA). As visões Contábil e Fiscal ficam nos seus módulos.
// ============================================================================

import React, { useState, useCallback, useMemo } from 'react';
import { ArrowLeft, Search, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import useOrdenacaoTabela from '../useOrdenacaoTabela';

const ITENS_POR_PAGINA = 15;

// As colunas aqui são dinâmicas (vêm da procedure), então não informamos tipos:
// o hook usa auto-detecção (data → número → texto) por coluna. Const de módulo
// para identidade estável no useMemo do hook.
const TIPOS_ORDENACAO = {};

// Colunas numéricas da visão Financeira (formatadas como valor). Mantemos um
// superconjunto para o caso de a procedure devolver colunas extras no futuro.
const COLUNAS_VALOR = [
    'SOMA_VL_TOTAL', 'SOMA_VL_LIQUIDA', 'SOMA_VL_DESCONTO',
    'TOTAL_BRUTO', 'TOTAL_LIQUIDO'
];

const COR = '#16a39c';        // teal do Financeiro
const COR_ESCURA = '#0d7a74';

// Últimos 12 meses no formato que a procedure espera (AAAA_MM).
const gerarMeses = () => {
    const meses = [];
    const hoje = new Date();
    for (let i = 0; i < 12; i++) {
        const d = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
        meses.push(`${d.getFullYear()}_${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
    return meses;
};

const NOMES_MES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const rotularMes = (m) => {
    const [ano, mes] = m.split('_');
    return `${NOMES_MES[parseInt(mes, 10) - 1]}/${ano}`;
};

const EMPRESAS = [
    { value: 'Todas', label: 'Todas as Lojas' },
    { value: 'Hunger', label: 'Hunger' },
    { value: 'PG', label: 'PG' },
    { value: 'GC', label: 'GC' },
    { value: 'PM', label: 'PM' },
];

const formatarCelula = (chave, valor) => {
    if (valor === null || valor === undefined || valor === '') return '';
    if (COLUNAS_VALOR.includes(chave)) {
        const n = Number(valor);
        return Number.isNaN(n) ? valor : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return valor;
};

const FinanceiroRelatorioFechamento = ({ onVoltar }) => {
    const meses = useMemo(() => gerarMeses(), []);
    const [mesAno, setMesAno] = useState(meses[0]);
    const [empresa, setEmpresa] = useState('Todas');

    const [dados, setDados] = useState([]);
    const [loading, setLoading] = useState(false);
    const [erro, setErro] = useState('');
    const [pesquisado, setPesquisado] = useState(false);
    const [paginaAtual, setPaginaAtual] = useState(1);

    const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

    // Colunas dinâmicas (a procedure mora no banco; não fixamos schema).
    const colunas = useMemo(() => (dados.length > 0 ? Object.keys(dados[0]) : []), [dados]);

    // Ordenação clicável pelo cabeçalho. Ordena o conjunto INTEIRO antes de paginar.
    const { dadosOrdenados, ordenar, indicador } = useOrdenacaoTabela(dados, TIPOS_ORDENACAO);

    // Ordenar deve sempre levar de volta à primeira página (a ordem mudou).
    const ordenarEReiniciar = (campo) => { ordenar(campo); setPaginaAtual(1); };

    const totalPaginas = Math.max(1, Math.ceil(dados.length / ITENS_POR_PAGINA));
    const dadosPaginados = useMemo(() => {
        const inicio = (paginaAtual - 1) * ITENS_POR_PAGINA;
        return dadosOrdenados.slice(inicio, inicio + ITENS_POR_PAGINA);
    }, [dadosOrdenados, paginaAtual]);

    const montarQuery = () => {
        const qs = new URLSearchParams();
        qs.set('mesAno', mesAno);
        if (empresa) qs.set('empresa', empresa);
        return qs.toString();
    };

    const buscar = useCallback(async () => {
        setLoading(true); setErro(''); setPesquisado(true); setPaginaAtual(1);
        try {
            const r = await fetch(`/api/automacoes-financeiro/relatorio-fechamento?${montarQuery()}`, { headers: auth() });
            if (!r.ok) {
                const txt = await r.text();
                throw new Error(`HTTP ${r.status} — ${txt}`);
            }
            const data = await r.json();
            setDados(Array.isArray(data) ? data : []);
        } catch (e) {
            setErro(e.message);
            setDados([]);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mesAno, empresa]);

    const exportarXlsx = async () => {
        try {
            const r = await fetch(`/api/automacoes-financeiro/relatorio-fechamento?${montarQuery()}&formato=xlsx`, { headers: auth() });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Fechamento_Financeiro_${mesAno}_${empresa}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            setErro('Erro ao exportar: ' + e.message);
        }
    };

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <button onClick={onVoltar} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '8px 14px', background: '#fff', border: '1px solid #e2e8f0',
                    borderRadius: 8, fontWeight: 700, cursor: 'pointer',
                }}>
                    <ArrowLeft size={16} /> Voltar
                </button>
                <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>Fechamento Mensal — Visão Financeira</h3>
            </div>

            {/* Filtros */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr)) auto auto', gap: 12, alignItems: 'end' }}>
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Mês de apuração</label>
                        <select value={mesAno} onChange={(e) => setMesAno(e.target.value)}
                            style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 }}>
                            {meses.map((m) => <option key={m} value={m}>{rotularMes(m)}</option>)}
                        </select>
                    </div>
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Empresa</label>
                        <select value={empresa} onChange={(e) => setEmpresa(e.target.value)}
                            style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 }}>
                            {EMPRESAS.map((emp) => <option key={emp.value} value={emp.value}>{emp.label}</option>)}
                        </select>
                    </div>
                    <button onClick={buscar} disabled={loading} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '10px 16px', background: loading ? '#94a3b8' : COR_ESCURA, color: '#fff',
                        border: 'none', borderRadius: 8, fontWeight: 800, cursor: loading ? 'wait' : 'pointer',
                    }}>
                        <Search size={16} /> {loading ? 'Buscando…' : 'Buscar'}
                    </button>
                    <button onClick={exportarXlsx} disabled={loading || dados.length === 0} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '10px 16px', background: dados.length === 0 ? '#e2e8f0' : '#059669',
                        color: dados.length === 0 ? '#64748b' : '#fff',
                        border: 'none', borderRadius: 8, fontWeight: 800,
                        cursor: dados.length === 0 ? 'not-allowed' : 'pointer',
                    }}>
                        <Download size={16} /> Exportar XLSX
                    </button>
                </div>
            </div>

            {erro && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 12 }}>{erro}</div>}

            {pesquisado && !loading && (
                <div style={{ fontSize: 13, color: '#475569', marginBottom: 8 }}>
                    {dados.length} registro(s) encontrado(s).
                    {dados.length > 0 && ` · Página ${paginaAtual} de ${totalPaginas}`}
                </div>
            )}

            {pesquisado && !loading && dados.length === 0 && !erro && (
                <div style={{ padding: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, color: '#64748b' }}>
                    Nenhum dado encontrado para o mês/empresa selecionados.
                </div>
            )}

            {/* Tabela */}
            {dados.length > 0 && (
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead style={{ background: '#f0fdfa', position: 'sticky', top: 0 }}>
                            <tr>
                                {colunas.map((c) => (
                                    <th key={c} onClick={() => ordenarEReiniciar(c)} style={{
                                        padding: 10, textAlign: COLUNAS_VALOR.includes(c) ? 'right' : 'left',
                                        fontWeight: 800, color: COR_ESCURA,
                                        borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap',
                                        cursor: 'pointer', userSelect: 'none',
                                    }}>{c}{indicador(c)}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {dadosPaginados.map((row, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    {colunas.map((c) => (
                                        <td key={c} style={{
                                            padding: 10, color: '#0f172a',
                                            textAlign: COLUNAS_VALOR.includes(c) ? 'right' : 'left',
                                            whiteSpace: 'nowrap',
                                        }}>{formatarCelula(c, row[c])}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Paginação */}
            {dados.length > ITENS_POR_PAGINA && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
                    <button onClick={() => setPaginaAtual(1)} disabled={paginaAtual === 1} style={btnPaginacao(paginaAtual === 1)}>« Primeira</button>
                    <button onClick={() => setPaginaAtual((p) => Math.max(1, p - 1))} disabled={paginaAtual === 1} style={btnPaginacao(paginaAtual === 1)}>
                        <ChevronLeft size={14} /> Anterior
                    </button>
                    <span style={{ padding: '8px 14px', background: COR, color: '#fff', borderRadius: 6, fontWeight: 700, fontSize: 13 }}>
                        {paginaAtual} / {totalPaginas}
                    </span>
                    <button onClick={() => setPaginaAtual((p) => Math.min(totalPaginas, p + 1))} disabled={paginaAtual === totalPaginas} style={btnPaginacao(paginaAtual === totalPaginas)}>
                        Próxima <ChevronRight size={14} />
                    </button>
                    <button onClick={() => setPaginaAtual(totalPaginas)} disabled={paginaAtual === totalPaginas} style={btnPaginacao(paginaAtual === totalPaginas)}>Última »</button>
                </div>
            )}
        </div>
    );
};

const btnPaginacao = (desabilitado) => ({
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '8px 12px',
    background: desabilitado ? '#f1f5f9' : '#fff',
    color: desabilitado ? '#94a3b8' : '#475569',
    border: '1px solid #cbd5e1', borderRadius: 6,
    fontWeight: 700, fontSize: 13,
    cursor: desabilitado ? 'not-allowed' : 'pointer',
});

export default FinanceiroRelatorioFechamento;
